import React, { useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import useDragItem from '../../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../../stores/reducers/user-interface';
import { FEED_TYPES } from '../../../stores/reducers/shots';
import { GreenroomState as GS } from '../../../stores/types/types';
import { ThumbnailLocation } from '../../../types/enums';
import useShots from '../../../contexts/shots/provider';
import DefaultSafetyImage from '../../../images/default-safety-image.png';
import { useLivekit } from '../../../contexts/livekit/livekit-context';
import useAudioControls from '../srt-stream/use-audio-controls';
import { UpdateLivekitIngress } from '../../../connection/livekit';
import Icon, { COLORS, ICONS } from '../../general-components/icon';

interface Props {
	ingressStreamId: string;
	participantIdentity?: string;
	location: ThumbnailLocation;
}

export default function IngressStreamThumbnail({ ingressStreamId, participantIdentity, location }: Props) {
	const { FeedsTab, ShotModal } = ThumbnailLocation;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const livekitIngresses = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);
	const { shotModalShot: { shot_data: { shotFeeds: modalShotFeeds }}} = useSelector((s: GS) => s.ShotsReducer);
	const { currentShot: { shot_data: { shotFeeds: currentShotFeeds }}} = useShots();

	const feeds = useMemo(
		() => (location === ThumbnailLocation.ShotModal ? modalShotFeeds : currentShotFeeds),
		[location, modalShotFeeds, currentShotFeeds],
	);

	const inShot = useMemo(
		() => feeds.some((f) => f.feedType === FEED_TYPES.INGRESS_STREAM && f.ingressStreamId === ingressStreamId),
		[feeds, ingressStreamId],
	);

	const canDrag = useMemo(
		() => host && (location === FeedsTab || location === ShotModal),
		[host, location],
	);

	const { dragRef, isDragging } = useDragItem({ id: ingressStreamId, type: DRAG_TYPE.FEED_INGRESS_STREAM }, canDrag);

	const ingress = useMemo(
		() => livekitIngresses.find((i) => i.id === ingressStreamId) ?? null,
		[livekitIngresses, ingressStreamId],
	);

	const { participantsMap } = useLivekit();
	const videoTrack = participantIdentity ? participantsMap.get(participantIdentity)?.videoTrack : undefined;
	const videoRef = useRef<HTMLVideoElement>(null);

	const { localVolume, localMuted, handleMuteChange, handleVolumeChange } = useAudioControls(
		ingress,
		({ volume, muted }) => {
			if (!token || !meeting || !ingress) return;
			UpdateLivekitIngress(token, meeting.meeting, ingress.id, { volume, muted });
		},
	);

	useEffect(() => {
		const element = videoRef.current;
		if (!element || !videoTrack) return;
		videoTrack.attach(element);
		return () => { videoTrack.detach(element); };
	}, [videoTrack]);

	return (
		<>
			{isDragging && <div className="video-thumb-drag-placeholder" />}
			<div
				ref={dragRef}
				className={classNames('video-thumb', {
					'is-dragging': isDragging,
					'cursor-grab': canDrag,
				})}
			>
				<div className="video-wrapper">
					<div className="video-overlay flex-center">
						{videoTrack ? (
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								className="width-full height-full"
								style={{ objectFit: 'cover' }}
							/>
						) : (
							<div className="poster">
								<img
									className="poster-image"
									src={DefaultSafetyImage}
									alt="LiveKit Stream"
									onError={({ currentTarget }) => {
										currentTarget.src = DefaultSafetyImage;
									}}
								/>
							</div>
						)}
						<div className="video-overlay-bottom">
							<label className="bl-font-l1">{'LiveKit Stream'}</label>
						</div>
						{location === ThumbnailLocation.ShotTab && (
							<div
								className="flex-center flex-column width-full padding-button-small background-transparent"
								style={{ background: 'rgba(0, 0, 0, 0.8)' }}
							>
								<div className="align-items-center position-relative vol-wrap">
									<div
										className={classNames('flex-center', { localMuted })}
										onClick={handleMuteChange}
									>
										<Icon
											name={localMuted ? ICONS.AUDIO_MUTED : ICONS.SOUND}
											size={18}
											color={localMuted ? COLORS.GRAY : COLORS.WHITE}
										/>
									</div>
									<input
										type="range"
										className="volume"
										onChange={handleVolumeChange}
										value={localMuted ? 0 : localVolume}
										step={0.01}
										min={0}
										max={1}
										style={{ background: 'white', marginLeft: '8px' }}
									/>
								</div>
							</div>
						)}
					</div>
					<div className={classNames('selection-border', inShot && 'in-shot')} />
				</div>
			</div>
		</>
	);
}